import { observer }              from 'mobx-react-lite';
import React                     from 'react';
import View                      from '@widesk-ui/components/View';
import Button                    from '@widesk-ui/components/Button';
import Alert                     from '@widesk-ui/components/Alert';
import Checkbox                  from '@widesk-ui/components/form/Checkbox';
import { message }               from '@widesk-ui/hooks/useMessage';
import { planCapture }           from '@tokamap/core';
import type { MercatorBbox }     from '@tokamap/core';
import type { Shape }            from '@tokamap/core';
import type { TileLayerName }    from '@tokamap/core';
import SwitchField               from '@/TokamapEditor/ux/SwitchField';
import currentEditorStore        from '@/TokamapEditor/stores/currentEditorStore';
import currentProjectEditorStore from '@/TokamapEditor/stores/currentProjectEditorStore';
import leftSidebarStore          from '@/TokamapEditor/stores/leftSidebarStore';
import toggleRegionTool          from '@/TokamapEditor/helpers/toggleRegionTool';
import { captureImportSource }   from '@/TokamapEditor/helpers/planCapture';
import { regionSpanMeters }      from '@/TokamapEditor/helpers/planCapture';
import { imagesInRegion }        from '@/TokamapEditor/helpers/planCapture';
import { createPlanImport }      from '@/MultiUserEditor/client/helpers/client/dxf/dxfApi';

/**
 * **Analyser une zone de la carte** — l'autre entrée du parcours d'import de plan
 * (`services/map/docs/PLAN_ANALYSE_ZONE_CARTE.md`).
 *
 * Au lieu de déposer un fichier, l'opérateur pose une image sur la carte, entoure la partie à
 * analyser, et c'est la CAPTURE de cette zone qui part comme plan. Ce panneau ne couvre que ce
 * bout-là : entourer, choisir ce qui entre dans l'image, vérifier, envoyer. Dès l'import créé, il
 * passe la main à `PlanImportSidebar`, qui porte déjà la vérification et la génération — les
 * réécrire ici en donnerait deux versions à maintenir.
 *
 * ## Ce qu'on ne fait pas
 *
 * Aucun placement à tracer : la zone étant déjà en coordonnées de la carte, le serveur déduit le
 * descripteur en gelant sa transform (`CapturePlacement`, service `map`). C'est tout l'intérêt du
 * parcours, et le geste le plus fragile de l'import par fichier disparaît avec.
 */
const PlanZoneSidebar = observer(() => {
	const instance = currentEditorStore.get;

	const [region, setRegion] = React.useState<MercatorBbox | null>(instance?.mapAdapter.getRegion() ?? null);
	const [toolActive, setToolActive] = React.useState<boolean>(instance?.mapAdapter.isDrawingRegion() ?? false);
	const [withBasemap, setWithBasemap] = React.useState(false);
	const [excluded, setExcluded] = React.useState<Record<string, true>>({});
	const [preview, setPreview] = React.useState<string | null>(null);
	const [busy, setBusy] = React.useState(false);
	const [error, setError] = React.useState<string | null>(null);

	const basemap: TileLayerName = instance?.mapAdapter.getTileLayerName() ?? 'unset';

	// L'emprise et l'outil vivent dans l'adaptateur, pas ici : l'opérateur peut retracer, tirer une
	// poignée, ou ranger l'outil d'un Escape sans passer par ce panneau.
	React.useEffect(() => {
		if (!instance) return;

		return instance.mapAdapter.on(event => {
			if (event.type === 'region:changed') return setRegion(event.region);
			if (event.type === 'region:tool-changed') return setToolActive(event.active);
		});
	}, [instance]);

	// Le panneau ARME l'outil en s'ouvrant : on vient d'ouvrir « Zone », il n'y a rien d'autre à
	// vouloir faire. Il le range en se fermant, mais laisse l'emprise — la reperdre à chaque
	// aller-retour vers un autre panneau serait une punition.
	React.useEffect(() => {
		if (!instance || instance.mapAdapter.isDrawingRegion()) return;
		toggleRegionTool(instance);

		return () => instance.mapAdapter.stopDrawingRegion();
	}, [instance]);

	// Un aperçu ne vaut que pour la zone et les réglages qui l'ont produit.
	React.useEffect(() => {
		setPreview(current => {
			if (current) URL.revokeObjectURL(current);

			return null;
		});
	}, [region, withBasemap, excluded]);

	/**
	 * Les formes-image de la carte, celles qu'on peut faire entrer dans la capture.
	 *
	 * Toutes, pas seulement celles qui touchent la zone : une image hors cadre ne se voit tout
	 * simplement pas dans la capture (elle est hors bbox), et calculer une intersection pour
	 * raccourcir une liste ferait écrire une géométrie de plus pour aucun effet visible.
	 */
	const imageShapes: Shape[] = (instance?.shapeStore.getAll() ?? []).filter(shape => shape.image?.src);
	const shapeIds = imageShapes.filter(shape => !excluded[shape.id]).map(shape => shape.id);

	const plan = region ? safePlan(region) : null;
	const span = region ? regionSpanMeters(region) : null;

	/**
	 * Ce que l'analyse d'image verrait vraiment de cette zone — et donc s'il vaut la peine de
	 * l'envoyer.
	 *
	 * La détection serveur travaille par aplats de couleur. Sans image posée dans la zone, il ne
	 * reste que le fond de carte, et aucun fond ne se détecte : OSM est du gris sur du gris (les
	 * deux teintes fusionnent, il ne reste qu'une couleur, donc zéro calque), et sans fond du tout
	 * la capture est blanche. Dans ces deux cas l'analyse répond « 0 calque(s) », toujours — la
	 * laisser partir, c'est faire créer un import et attendre pour un refus connu d'avance.
	 *
	 * Le satellite, lui, n'est pas exclu : une photo porte de vraies couleurs. Le résultat est
	 * rarement bon, mais c'est un jugement sur le dessin, pas une impossibilité — on prévient sans
	 * empêcher.
	 */
	const analysable = ((): { ok: boolean; reason?: string } => {
		if (!instance || !region) return { ok: false };

		const inZone = imagesInRegion(instance.mapAdapter.imagePlacementsForCapture(shapeIds), region);
		if (inZone.length > 0) return { ok: true };

		const capturedBasemap = withBasemap ? basemap : 'unset';
		if ('osm' === capturedBasemap) {
			return {
				ok: false,
				reason: 'Cette zone ne contient aucune image : l’analyse ne verrait que le fond OSM, '
					+ 'dont le bâti et le sol fusionnent en une seule couleur à la détection — elle '
					+ 'répondrait « 0 calque ». Pour reprendre ce bâti, utilisez la '
					+ '« Baguette magique » du menu Tracer (W) : il est lu en vecteur, sans détection.',
			};
		}

		if ('unset' === capturedBasemap) {
			return {
				ok: false,
				reason: 'Cette zone ne contient aucune image et aucun fond de carte : la capture serait '
					+ 'blanche. Posez une image sur la carte, ou incluez le fond.',
			};
		}

		return { ok: true };
	})();

	const capture = async () => {
		if (!instance || !region) return null;

		return captureImportSource({
			adapter: instance.mapAdapter,
			bbox: region,
			basemap: withBasemap ? basemap : 'unset',
			shapeIds,
		});
	};

	async function run<T>(work: () => Promise<T>, fallback: string): Promise<T | null> {
		setBusy(true);
		setError(null);
		try {
			return await work();
		} catch (e) {
			setError(e instanceof Error ? e.message : fallback);

			return null;
		} finally {
			setBusy(false);
		}
	}

	const showPreview = () => run(async () => {
		const shot = await capture();
		if (shot) setPreview(URL.createObjectURL(shot.file));
	}, 'Échec de la capture de la zone.');

	/**
	 * Escape efface le cadre de la zone — le même geste que le bouton « Effacer la zone ».
	 *
	 * Le raccourci s'écoute sur la FENÊTRE, comme celui de la file de doutes (`PlanDoubtQueue`) : le
	 * cadre se trace à la souris sur la carte, le panneau n'a jamais le
	 * focus. Il n'est branché que tant qu'il y a un cadre à retirer, sans quoi il mangerait l'Escape
	 * des autres outils de l'éditeur.
	 *
	 * L'outil zone, lui, RESTE armé : effacer un cadre, c'est vouloir en tracer un autre, et obliger
	 * à repasser par « Activer l'outil zone » entre deux essais annulerait le service rendu.
	 * `MapLibreAdapter` écoute pourtant Escape de son côté et y range l'outil (sans toucher à
	 * l'emprise, c'est son parti pris) : on le rend donc APRÈS lui. La microtask est là pour ça, et
	 * elle évite de parier sur l'ordre des abonnements — `startDrawingRegion` étant idempotent, le
	 * rendre alors qu'il est déjà là ne coûte rien.
	 */
	React.useEffect(() => {
		if (!instance || !region) return;

		const onKey = (event: KeyboardEvent) => {
			if ('Escape' !== event.key || event.defaultPrevented) return;

			const target = event.target as HTMLElement | null;
			if (target && ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)) return;

			instance.mapAdapter.setRegion(null);
			queueMicrotask(() => instance.mapAdapter.startDrawingRegion());
		};

		window.addEventListener('keydown', onKey);

		return () => window.removeEventListener('keydown', onKey);
	}, [instance, region]);

	/**
	 * Capture, crée l'import, puis passe la main.
	 *
	 * `captureBbox` bascule l'import dans l'autre parcours côté serveur : c'est sa seule présence
	 * qui fait déduire le placement à l'aperçu, et qui dispense la génération d'en recevoir un.
	 */
	const analyse = () => run(async () => {
		const projectUrn = currentProjectEditorStore.get?.project?.urn;
		if (!projectUrn) throw new Error('Projet introuvable.');

		const shot = await capture();
		if (!shot) return;

		const created = await createPlanImport({
			planFile: shot.file,
			mapUrn: currentProjectEditorStore.get.currentMap.urn,
			projectUrn,
			captureBbox: shot.plan.bbox,
		});

		message.success('Zone capturée : analyse en cours.');
		leftSidebarStore.openPlanImport({ adopt: { importId: created.importId, typeReference: created.typeReference, capture: true, file: shot.file } });
	}, 'Échec de l’analyse de la zone.');

	if (!instance) return null;

	return (
		<View p="md" gap="md">
			{!!error && <Alert type="error" message={error} />}

			{!region && (
				<Alert
					type="info"
					message="Entourez la zone à analyser"
					description={
						toolActive
							? 'Glissez sur la carte pour tracer un rectangle autour de la partie du plan à détecter.'
							: 'L’outil est rangé. Réactivez-le pour tracer la zone.'
					}
				/>
			)}

			{!toolActive && (
				<Button onClick={() => toggleRegionTool(instance)}>Activer l’outil zone</Button>
			)}

			{!!region && !!span && !!plan && (
				<>
					<View gap="xs">
						<View bold>Zone entourée</View>
						<View size="small">{Math.round(span.width)} × {Math.round(span.height)} m</View>
						{/* La résolution est celle que la capture retiendra vraiment : `planCapture` applique
						    déjà ses plafonds (côté, zoom) et l'arrondi au pixel. */}
						<View size="small">Capture : {plan.width} × {plan.height} px</View>
					</View>

					<SwitchField
						label="Inclure le fond de carte"
						help={basemap === 'unset'
							? 'aucun fond de carte n’est affiché — rien à inclure'
							: 'ses aplats entrent dans la détection des couleurs'}
						value={withBasemap && basemap !== 'unset'}
						onChange={setWithBasemap}
					/>

					{imageShapes.length > 0 && (
						<View gap="xs">
							<View bold>Images à analyser</View>
							{imageShapes.map(shape => (
								<View key={shape.id} row gap="xs" centerV>
									<Checkbox
										value={!excluded[shape.id]}
										onChange={() => setExcluded(current => {
											const next = { ...current };
											if (next[shape.id]) delete next[shape.id];
											else next[shape.id] = true;

											return next;
										})}
									/>
									<View size="small">{imageLabel(shape)}</View>
								</View>
							))}
						</View>
					)}

					<Button onClick={showPreview} loading={busy}>Aperçu de la capture</Button>

					{!!preview && (
						// Le meilleur garde-fou du parcours : ce qu'on voit ici est exactement ce qui part.
						<img src={preview} alt="Aperçu de la zone capturée" style={{ width: '100%', border: '1px solid #ddd' }} />
					)}

					{!!analysable.reason && <Alert type="warning" message={analysable.reason} />}

					<View row gap="sm">
						<Button type="primary" onClick={analyse} loading={busy} disabled={!analysable.ok}>
							Analyser cette zone
						</Button>
						<Button onClick={() => instance.mapAdapter.setRegion(null)} disabled={busy}>Effacer la zone</Button>
					</View>
				</>
			)}
		</View>
	);
});

/**
 * De quoi reconnaître une image dans la liste : son texte si elle en porte un, sinon le nom du
 * fichier — l'identifiant technique de la forme ne dit rien à personne.
 */
function imageLabel(shape: Shape): string {
	if (shape.text?.text) return shape.text.text;

	const src = shape.image?.src ?? '';
	const name = decodeURIComponent(src.split('?')[0] ?? '').split('/').pop();

	return name || shape.id;
}

/**
 * Dimensions de la capture, ou `null` si la zone est dégénérée.
 *
 * `planCapture` REFUSE une zone vide (il lève) plutôt que de rendre une image de zéro pixel. Ici on
 * ne fait qu'afficher un récapitulatif : un rectangle en cours de tracé peut être vide une frame,
 * et ce n'est pas une erreur à montrer.
 */
function safePlan(bbox: MercatorBbox): { width: number; height: number } | null {
	try {
		return planCapture(bbox);
	} catch {
		return null;
	}
}

export default PlanZoneSidebar;
